// pages/ChatSpace/ChatSpaceSidebar.js
import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Typography,
    IconButton,
    TextField,
    InputAdornment,
    CircularProgress,
    Avatar,
    Drawer,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";

const colors = {
    textPrimary: '#1e293b',
    textSecondary: '#475569',
    textTertiary: '#94a3b8',
    accent: '#3b82f6',
};

export default function ChatSpaceSidebar({
    isOpen,
    isCollapsed = false,
    onToggle,
    onConversationSelect,
    currentSessionCode,
    isMobile = false,
}) {
    const [conversations, setConversations] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [loading, setLoading] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [editTitle, setEditTitle] = useState('');

    const userStr = localStorage.getItem('user');
    const user = userStr ? JSON.parse(userStr) : null;
    const fname = user?.profile?.fname || '';
    const lname = user?.profile?.lname || '';
    const fullName = `${fname} ${lname}`.trim();

    useEffect(() => {
        if (isOpen) loadConversations();
    }, [isOpen]);

    useEffect(() => {
        if (isOpen && currentSessionCode) loadConversations();
    }, [currentSessionCode]);

    const loadConversations = async () => {
        setLoading(true);
        try {
            const response = await axiosInstance.get('/api/chatspace/conversations');
            if (response.data.success) {
                setConversations(response.data.data);
            }
        } catch (error) {
            console.error('Failed to load conversations:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleNewChat = () => {
        onConversationSelect(null);
        if (isMobile) onToggle();
    };

    const handleSelect = (sessionCode) => {
        onConversationSelect(sessionCode);
        if (isMobile) onToggle();
    };

    const handleDelete = async (sessionCode, e) => {
        e.stopPropagation();
        if (!window.confirm('Delete this conversation?')) return;
        try {
            const resp = await axiosInstance.delete(`/api/chatspace/conversations/${sessionCode}`);
            if (resp.data.success) {
                setConversations((prev) => prev.filter((c) => c.session_code !== sessionCode));
                if (sessionCode === currentSessionCode) handleNewChat();
            }
        } catch (error) {
            console.error('Failed to delete conversation:', error);
        }
    };

    const startEditing = (conv, e) => {
        e.stopPropagation();
        setEditingId(conv.session_code);
        setEditTitle(conv.title);
    };

    const handleRename = async (sessionCode) => {
        if (!editTitle.trim()) return;
        try {
            const resp = await axiosInstance.patch(`/api/chatspace/conversations/${sessionCode}/title`, {
                title: editTitle.trim(),
            });
            if (resp.data.success) {
                setConversations((prev) =>
                    prev.map((c) =>
                        c.session_code === sessionCode ? { ...c, title: editTitle.trim() } : c
                    )
                );
                setEditingId(null);
            }
        } catch (error) {
            console.error('Failed to rename:', error);
        }
    };

    const filtered = conversations.filter((c) =>
        c.title.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const formatTime = (timestamp) => {
        const date = new Date(timestamp);
        const now = new Date();
        const diff = now - date;
        const mins = Math.floor(diff / 60000);
        const hrs = Math.floor(diff / 3600000);
        const days = Math.floor(diff / 86400000);
        if (mins < 60) return `${mins}m ago`;
        if (hrs < 24) return `${hrs}h ago`;
        if (days < 7) return `${days}d ago`;
        return date.toLocaleDateString();
    };

    const sidebarContent = (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', fontFamily }}>
            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: isCollapsed ? 'center' : 'space-between',
                    px: 2,
                    py: 1.5,
                    borderBottom: '1px solid rgba(0,0,0,0.06)',
                }}
            >
                {!isCollapsed && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ChatBubbleOutlineIcon sx={{ fontSize: 18, color: colors.accent }} />
                        <Typography sx={{ fontFamily, fontSize: '0.875rem', fontWeight: 600, color: colors.textPrimary }}>
                            Chats
                        </Typography>
                    </Box>
                )}
                <IconButton
                    size="small"
                    onClick={onToggle}
                    title={isCollapsed ? 'Expand' : 'Collapse'}
                    sx={{
                        color: colors.textTertiary,
                        '&:hover': { bgcolor: 'rgba(59,130,246,0.1)', color: colors.accent },
                    }}
                >
                    <MenuOpenIcon
                        sx={{
                            fontSize: 18,
                            transform: isCollapsed ? 'rotate(180deg)' : 'none',
                            transition: 'transform 0.3s',
                        }}
                    />
                </IconButton>
            </Box>

            {/* New Chat */}
            <Box sx={{ p: 1.5, px: isCollapsed ? 1 : 1.5 }}>
                {isCollapsed ? (
                    <IconButton
                        onClick={handleNewChat}
                        sx={{
                            width: '100%',
                            background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
                            color: '#fff',
                            borderRadius: '10px',
                            boxShadow: '0 2px 8px rgba(59,130,246,0.3)',
                            '&:hover': { background: 'linear-gradient(135deg, #1d4ed8, #3b82f6)' },
                        }}
                    >
                        <AddIcon />
                    </IconButton>
                ) : (
                    <Button
                        fullWidth
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleNewChat}
                        sx={{
                            fontFamily,
                            borderRadius: '10px',
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: '0.8rem',
                            background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
                            boxShadow: '0 2px 8px rgba(59,130,246,0.3)',
                            '&:hover': {
                                background: 'linear-gradient(135deg, #1d4ed8, #3b82f6)',
                                transform: 'translateY(-1px)',
                            },
                            transition: 'all 0.2s ease',
                        }}
                    >
                        New Chat
                    </Button>
                )}
            </Box>

            {/* Search */}
            {!isCollapsed && (
                <Box sx={{ px: 1.5, pb: 1.5 }}>
                    <TextField
                        size="small"
                        fullWidth
                        placeholder="Search..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ fontSize: 16, color: colors.textTertiary }} />
                                </InputAdornment>
                            ),
                            sx: {
                                fontFamily,
                                fontSize: '0.75rem',
                                borderRadius: '10px',
                                bgcolor: 'rgba(0,0,0,0.02)',
                                '& fieldset': { borderColor: 'rgba(0,0,0,0.08)' },
                                '&:hover fieldset': { borderColor: 'rgba(59,130,246,0.3) !important' },
                                '&.Mui-focused fieldset': { borderColor: `${colors.accent} !important` },
                            },
                        }}
                    />
                </Box>
            )}

            {/* Conversations */}
            <Box sx={{
                flex: 1,
                overflow: 'auto',
                px: isCollapsed ? 0.5 : 1,
                '&::-webkit-scrollbar': { width: '4px' },
                '&::-webkit-scrollbar-track': { background: 'transparent' },
                '&::-webkit-scrollbar-thumb': {
                    background: 'rgba(0,0,0,0.1)',
                    borderRadius: '4px',
                    '&:hover': { background: 'rgba(0,0,0,0.2)' },
                },
            }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                        <CircularProgress size={24} sx={{ color: colors.accent }} />
                    </Box>
                ) : filtered.length === 0 ? (
                    !isCollapsed && (
                        <Box sx={{ textAlign: 'center', py: 6 }}>
                            <ChatBubbleOutlineIcon sx={{ fontSize: 40, color: 'rgba(0,0,0,0.1)', mb: 1 }} />
                            <Typography sx={{ fontFamily, fontSize: '0.75rem', color: colors.textTertiary }}>
                                {searchQuery ? 'No conversations found' : 'No conversations yet'}
                            </Typography>
                        </Box>
                    )
                ) : (
                    filtered.map((conv) => {
                        const isActive = conv.session_code === currentSessionCode;
                        return (
                            <Box
                                key={conv.session_code}
                                onClick={() => editingId !== conv.session_code && handleSelect(conv.session_code)}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: 1,
                                    p: isCollapsed ? 1 : 1.5,
                                    mb: 0.5,
                                    borderRadius: '10px',
                                    cursor: 'pointer',
                                    justifyContent: isCollapsed ? 'center' : 'flex-start',
                                    bgcolor: isActive ? 'rgba(59,130,246,0.08)' : 'transparent',
                                    border: isActive ? '1px solid rgba(59,130,246,0.15)' : '1px solid transparent',
                                    '&:hover': {
                                        bgcolor: isActive ? 'rgba(59,130,246,0.12)' : 'rgba(0,0,0,0.03)',
                                    },
                                    '&:hover .conv-actions': { opacity: 1 },
                                    transition: 'all 0.15s ease',
                                }}
                            >
                                <ChatBubbleOutlineIcon
                                    sx={{
                                        fontSize: 14,
                                        color: isActive ? colors.accent : colors.textTertiary,
                                        mt: 0.25,
                                        flexShrink: 0,
                                    }}
                                />
                                {!isCollapsed && (
                                    <>
                                        {editingId === conv.session_code ? (
                                            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <TextField
                                                    size="small"
                                                    value={editTitle}
                                                    onChange={(e) => setEditTitle(e.target.value)}
                                                    onKeyPress={(e) => e.key === 'Enter' && handleRename(conv.session_code)}
                                                    onClick={(e) => e.stopPropagation()}
                                                    autoFocus
                                                    inputProps={{ maxLength: 60, style: { fontFamily, fontSize: '0.75rem', padding: '4px 8px' } }}
                                                    sx={{ flex: 1 }}
                                                />
                                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleRename(conv.session_code); }}>
                                                    <CheckIcon sx={{ fontSize: 14, color: '#059669' }} />
                                                </IconButton>
                                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); setEditingId(null); }}>
                                                    <CloseIcon sx={{ fontSize: 14, color: colors.textTertiary }} />
                                                </IconButton>
                                            </Box>
                                        ) : (
                                            <>
                                                <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                                                    <Typography
                                                        noWrap
                                                        sx={{
                                                            fontFamily,
                                                            display: 'block',
                                                            fontSize: '0.75rem',
                                                            fontWeight: isActive ? 600 : 500,
                                                            color: isActive ? colors.textPrimary : colors.textSecondary,
                                                        }}
                                                    >
                                                        {conv.title || 'New Conversation'}
                                                    </Typography>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        <AccessTimeIcon sx={{ fontSize: 11, color: colors.textTertiary }} />
                                                        <Typography sx={{ fontFamily, fontSize: '0.65rem', color: colors.textTertiary }}>
                                                            {formatTime(conv.last_message_at)}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                                <Box
                                                    className="conv-actions"
                                                    sx={{
                                                        display: 'flex',
                                                        opacity: 0,
                                                        transition: 'opacity 0.15s',
                                                        flexShrink: 0,
                                                    }}
                                                >
                                                    <IconButton size="small" onClick={(e) => startEditing(conv, e)} sx={{ p: 0.25 }}>
                                                        <EditIcon sx={{ fontSize: 12, color: colors.textTertiary }} />
                                                    </IconButton>
                                                    <IconButton size="small" onClick={(e) => handleDelete(conv.session_code, e)} sx={{ p: 0.25 }}>
                                                        <DeleteOutlineIcon sx={{ fontSize: 12, color: '#dc2626' }} />
                                                    </IconButton>
                                                </Box>
                                            </>
                                        )}
                                    </>
                                )}
                            </Box>
                        );
                    })
                )}
            </Box>

            {/* Footer */}
            <Box sx={{ px: 1.5, py: 1.25, borderTop: '1px solid rgba(0,0,0,0.06)' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: isCollapsed ? 'center' : 'flex-start' }}>
                    <Avatar
                        sx={{
                            width: 28,
                            height: 28,
                            bgcolor: colors.accent,
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            fontFamily,
                        }}
                    >
                        {fname?.charAt(0) || user?.user_email?.charAt(0) || 'U'}
                    </Avatar>
                    {!isCollapsed && (
                        <Box sx={{ minWidth: 0, overflow: 'hidden' }}>
                            <Typography noWrap sx={{ fontFamily, fontSize: '0.75rem', fontWeight: 600, color: colors.textPrimary }}>
                                {fullName || user?.user_email || 'User'}
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Box>
        </Box>
    );

    // Mobile: use Drawer
    if (isMobile) {
        return (
            <Drawer
                anchor="left"
                open={isOpen}
                onClose={onToggle}
                PaperProps={{
                    sx: {
                        width: 260,
                        borderRadius: '0 14px 14px 0',
                    }
                }}
            >
                {sidebarContent}
            </Drawer>
        );
    }

    // Desktop: inline
    return (
        <Box
            sx={{
                width: isCollapsed ? 56 : 240,
                flexShrink: 0,
                height: '100%',
                transition: 'width 0.3s ease',
                bgcolor: 'rgba(255,255,255,0.8)',
                backdropFilter: 'blur(12px)',
                borderRadius: '14px',
                border: '1px solid rgba(0,0,0,0.06)',
                overflow: 'hidden',
            }}
        >
            {sidebarContent}
        </Box>
    );
}
